import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  BackArrow,
  Input,
  Select,
  CheckBox,
  Button,
  TopProgress
} from '../../../../components'
import {
  CreateContainer,
  Container,
  TitleBig,
  FormWrapper,
  Info,
  InfoTitle,
  InfoText,
} from './styles'
import * as yup from 'yup';
import { useFormik } from 'formik';
import { billingService } from '../../../../api'
import { useUi } from '../../../../context/ui.context'
import { usePageTitle } from '../../../../context/navigationContext'

const ProductCreateEdit: FC = () => {
  const navigate = useNavigate()
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const { productId } = useParams()
  const isEdit = !!productId
  usePageTitle(isEdit ? `${t("EDIT_PRODUCT")}` : t("CREATE_PRODUCT"));

  const [product, setProduct] = useState<any>(null)
  const [createOptions, setCreateOptions] = useState<any>(null)
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)

  const [taxable, setTaxable] = useState<boolean>(true)
  const [requiresDomain, setRequiresDomain] = useState<boolean>(false)
  const [creditOnDowngrade, setCreditOnDowngrade] = useState<boolean>(false)
  const [hideServices, setHideServices] = useState<boolean>(false)
  const [allowZeroPriceOrder, setAllowZeroPriceOrder] = useState<boolean>(false)

  const [initialValues, setInitialValues] = useState<any>({
    name: '',
    code: '',
    description: '',
    billing_type: '',
    group: '',
    module: '',
    product_type: '',
    status: '',
    price_model: '',
    auto_setup: '',
    welcome_email: '',
  })

  const loadData = async () => {
    try {
      const optionsRes = await billingService.getProductCreateOptions()
      const opts = optionsRes.data

      setCreateOptions(opts)

      if (isEdit && productId) {
        const productRes = await billingService.getProduct(productId)
        const prod = productRes.data
        setProduct(prod)
        setInitialValues({
          name: prod.name || '',
          code: prod.code || '',
          description: prod.description || '',
          billing_type: prod.billing_type || '',
          group: prod.group?.id || prod.group || '',
          module: prod.module?.id || prod.module || '',
          product_type: prod.product_type || '',
          status: prod.status || '',
          price_model: prod.price_model || '',
          auto_setup: prod.auto_setup || '',
          welcome_email: prod.welcome_email || '',
        })
        setTaxable(prod.taxable ?? true)
        setRequiresDomain(prod.requires_domain ?? false)
        setCreditOnDowngrade(prod.credit_on_downgrade ?? false)
        setHideServices(prod.hide_services ?? false)
        setAllowZeroPriceOrder(prod.allow_zero_price_order ?? false)
      } else {
        const modules = opts.modules ? Object.values(opts.modules) as any[] : []
        const defaultModule = modules.find((m: any) => m.plugin === 'todo') || modules[0]
        const defaultPriceModel = opts.price_models?.find((p: any) => p[0] === 'fixed_and_dynamic') || opts.price_models?.[0]

        setInitialValues({
          name: '',
          code: '',
          description: '',
          billing_type: opts.billing_types?.[0]?.[0] || '',
          group: opts.groups?.[0]?.id || '',
          module: defaultModule?.id || '',
          product_type: opts.product_types?.[0]?.[0] || '',
          status: opts.statuses?.[0]?.[0] || '',
          price_model: defaultPriceModel?.[0] || opts.price_models?.[0]?.[0] || '',
          auto_setup: opts.auto_setups?.[0]?.[0] || '',
          welcome_email: '',
        })
      }
      setPageLoading(false)
    } catch (e: any) {
      console.error('Error loading data:', e)
      setPageLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const onSubmit = (values: any) => {
    setLoading(true)
    const data = {
      ...values,
      taxable,
      requires_domain: requiresDomain,
      credit_on_downgrade: creditOnDowngrade,
      hide_services: hideServices,
      allow_zero_price_order: allowZeroPriceOrder,
    }

    const apiCall = isEdit
      ? billingService.updateProduct(productId!, data)
      : billingService.createProduct(data)

    apiCall
      .then((res) => {
        setLoadingComplete(true)
        viewAlert({
          severity: 'success',
          message: isEdit ? t("PRODUCT_UPDATED") : t("PRODUCT_CREATED")
        })
        setTimeout(() => {
          if (isEdit) {
            navigate(`/billing/products/${productId}`)
          } else {
            navigate(`/billing/products/${res.data.id}`)
          }
          setLoadingComplete(false)
          setLoading(false)
        }, 1000)
      })
      .catch((err: any) => {
        setLoading(false)
        viewAlert({
          severity: 'error',
          message: err?.response?.data?.detail || err?.response?.data?.non_field_errors?.[0] || `${t("ERROR_OCCURRED")}`
        })
      })
  }

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup.string().required(`${t('REQUIRED_FIELD')}`),
      code: yup.string().required(`${t('REQUIRED_FIELD')}`),
      description: yup.string(),
      billing_type: yup.string().required(`${t('REQUIRED_FIELD')}`),
      group: yup.string().required(`${t('REQUIRED_FIELD')}`),
      module: yup.string().required(`${t('REQUIRED_FIELD')}`),
      product_type: yup.string().required(`${t('REQUIRED_FIELD')}`),
      status: yup.string().required(`${t('REQUIRED_FIELD')}`),
      price_model: yup.string().required(`${t('REQUIRED_FIELD')}`),
      auto_setup: yup.string().required(`${t('REQUIRED_FIELD')}`),
      welcome_email: yup.string(),
    }),
    initialValues: {
      ...initialValues,
    },
    onSubmit,
  });

  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  };

  const modules = createOptions?.modules
    ? Object.values(createOptions.modules).map((m: any) => ({
        label: m.name,
        value: m.id?.toString() || m.id,
      }))
    : []

  const groups = createOptions?.groups?.map((g: any) => ({
    label: g.name,
    value: g.id?.toString() || g.id,
  })) || []

  const mapOptions = (arr: any[]) =>
    arr?.map((item: any) => ({
      label: item[1],
      value: item[0],
    })) || []

  const welcomeEmailOptions = createOptions?.welcome_email
    ? [
        { label: t("NONE"), value: '' },
        ...Object.entries(createOptions.welcome_email).map(([key, val]) => ({
          label: val as string,
          value: key,
        }))
      ]
    : [{ label: t("NONE"), value: '' }]

  if (pageLoading) return <TopProgress loading={pageLoading} />

  return (
    <CreateContainer>
      <Container>
        <BackArrow fallbackPath="/billing/products" />
        <TitleBig>
          {isEdit ? `${t("EDIT_PRODUCT")} ${product?.name || ''}` : t("CREATE_PRODUCT")}
        </TitleBig>
        <FormWrapper>
          <Input
            required
            fieldName='name'
            title={t("NAME")}
            placeholder=''
            {...formikProps}
          />
          <Input
            required
            fieldName='code'
            title={t("PRODUCT_CODE")}
            placeholder=''
            {...formikProps}
          />
        </FormWrapper>
        <FormWrapper>
          <Input
            isTextArea
            fieldName='description'
            title={t("DESCRIPTION")}
            placeholder=''
            {...formikProps}
          />
        </FormWrapper>
        <FormWrapper>
          <Select
            required
            toolTip={false}
            data={mapOptions(createOptions?.billing_types || [])}
            fieldName='billing_type'
            selectedValue={values['billing_type']}
            onChange={(e: any) => handleChange('billing_type')(e)}
            {...formikProps}
            title={t("BILLING_TYPE")}
          />
        </FormWrapper>
        <FormWrapper>
          <Select
            required
            toolTip={false}
            data={groups}
            fieldName='group'
            selectedValue={values['group']?.toString()}
            onChange={(e: any) => handleChange('group')(e)}
            {...formikProps}
            title={t("PRODUCT_GROUP")}
          />
          <Select
            required
            toolTip={false}
            data={modules}
            fieldName='module'
            selectedValue={values['module']?.toString()}
            onChange={(e: any) => handleChange('module')(e)}
            {...formikProps}
            title={t("MODULE")}
          />
        </FormWrapper>
        <FormWrapper>
          <Select
            required
            toolTip={false}
            data={mapOptions(createOptions?.product_types || [])}
            fieldName='product_type'
            selectedValue={values['product_type']}
            onChange={(e: any) => handleChange('product_type')(e)}
            {...formikProps}
            title={t("PRODUCT_TYPE")}
          />
          <Select
            required
            toolTip={false}
            data={mapOptions(createOptions?.statuses || [])}
            fieldName='status'
            selectedValue={values['status']}
            onChange={(e: any) => handleChange('status')(e)}
            {...formikProps}
            title={t("STATUS")}
          />
        </FormWrapper>
        <FormWrapper>
          <Select
            required
            toolTip={false}
            data={mapOptions(createOptions?.price_models || [])}
            fieldName='price_model'
            selectedValue={values['price_model']}
            onChange={(e: any) => handleChange('price_model')(e)}
            {...formikProps}
            title={t("PRICE_MODEL")}
          />
          <Select
            required
            toolTip={false}
            data={mapOptions(createOptions?.auto_setups || [])}
            fieldName='auto_setup'
            selectedValue={values['auto_setup']}
            onChange={(e: any) => handleChange('auto_setup')(e)}
            {...formikProps}
            title={t("AUTO_SETUP")}
          />
        </FormWrapper>
        <FormWrapper>
          <Select
            toolTip={false}
            data={welcomeEmailOptions}
            fieldName='welcome_email'
            selectedValue={values['welcome_email']}
            onChange={(e: any) => handleChange('welcome_email')(e)}
            {...formikProps}
            title={t("WELCOME_EMAIL")}
          />
        </FormWrapper>
        <CheckBox
          value={allowZeroPriceOrder}
          onChange={() => setAllowZeroPriceOrder(!allowZeroPriceOrder)}
          title={t("ALLOW_ZERO_PRICE_ORDER")}
        />
        <CheckBox
          value={taxable}
          onChange={() => setTaxable(!taxable)}
          title={t("TAXABLE")}
        />
        <CheckBox
          value={requiresDomain}
          onChange={() => setRequiresDomain(!requiresDomain)}
          title={t("REQUIRES_DOMAIN")}
        />
        <CheckBox
          value={creditOnDowngrade}
          onChange={() => setCreditOnDowngrade(!creditOnDowngrade)}
          title={t("CREDIT_ON_DOWNGRADE")}
        />
        <CheckBox
          value={hideServices}
          onChange={() => setHideServices(!hideServices)}
          title={t("HIDE_SERVICES")}
        />
        <div style={{ display: 'flex', marginTop: '20px', gap: '10px' }}>
          <Button
            variant="primary"
            size="display"
            loading={loading}
            loadingComplete={loadingComplete}
            onClick={handleSubmit}
            title={isEdit ? t("SAVE") : t("CREATE")}
          />
          <Button
            variant="stroke"
            size="display"
            onClick={() => navigate('/billing/products')}
            title={t("CANCEL")}
          />
        </div>
      </Container>
      <Container style={{marginTop: '91px', flex: 'none', width: '400px'}}>
        <Info>
          <InfoTitle>{t("PRODUCT_INFO_CONFIGURE_TITLE")}</InfoTitle>
          <InfoText>{t("PRODUCT_INFO_CONFIGURE_TEXT")}</InfoText>
        </Info>
        <Info>
          <InfoTitle>{t("PRODUCT_INFO_GROUP_MODULE_TITLE")}</InfoTitle>
          <InfoText>{t("PRODUCT_INFO_GROUP_MODULE_TEXT")}</InfoText>
        </Info>
        <Info>
          <InfoTitle>{t("PRODUCT_INFO_TYPE_STATUS_TITLE")}</InfoTitle>
          <InfoText>{t("PRODUCT_INFO_TYPE_STATUS_TEXT")}</InfoText>
        </Info>
        <Info>
          <InfoTitle>{t("PRODUCT_INFO_PRICE_MODEL_TITLE")}</InfoTitle>
          <InfoText>{t("PRODUCT_INFO_PRICE_MODEL_TEXT")}</InfoText>
        </Info>
        <Info>
          <InfoTitle>{t("PRODUCT_INFO_ZERO_PRICE_TITLE")}</InfoTitle>
          <InfoText>{t("PRODUCT_INFO_ZERO_PRICE_TEXT")}</InfoText>
        </Info>
        <Info>
          <InfoTitle>{t("PRODUCT_INFO_CREDIT_DOWNGRADE_TITLE")}</InfoTitle>
          <InfoText>{t("PRODUCT_INFO_CREDIT_DOWNGRADE_TEXT")}</InfoText>
        </Info>
      </Container>
    </CreateContainer>
  )
}

export default ProductCreateEdit
